import { Transcript } from "../components/Transcript";
import { Reader } from "../components/Reader";
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { getSeries, getWork } from "../api";
import { Cover } from "../components/Cover";
import { ErrorState, LoadingState } from "../components/PageState";
import { WorkCard } from "../components/WorkCard";
import { useLibrary } from "../contexts/LibraryContext";
import { usePlayer } from "../contexts/PlayerContext";
import type { CanonicalWork } from "../generated/contracts";
import { usePageMeta } from "../hooks/usePageMeta";
import { assignArtwork } from "../artwork";

const formatLabel = (kind: string, label?: string | null) =>
  label || kind.toUpperCase();

export function WorkPage() {
  const navigate = useNavigate(),
    location = useLocation();
  const { slug = "" } = useParams();
  const [work, setWork] = useState<
    (CanonicalWork & { related?: CanonicalWork[] }) | null
  >(null);
  const [seriesQueue, setSeriesQueue] = useState<CanonicalWork[]>([]);
  const [error, setError] = useState("");
  const library = useLibrary();
  const player = usePlayer();
  usePageMeta(
    work?.title || "Archive work",
    work?.description || "Canonical archive work and available source formats.",
    `/works/${slug}`,
  );
  useEffect(() => {
    setWork(null);
    setError("");
    let active = true;
    getWork(slug)
      .then((w) => {
        if (!active) return;
        if (w.slug !== slug) {
          navigate(`/works/${w.slug}${location.search}${location.hash}`, {
            replace: true,
          });
          return;
        }
        setWork(w);
      })
      .catch((reason) => {
        if (active) setError(String(reason));
      });
    return () => {
      active = false;
    };
  }, [slug]);
  useEffect(() => {
    if (work?.series?.slug)
      getSeries(work.series.slug, null, work.id)
        .then((series) => setSeriesQueue(series.items))
        .catch(() => setSeriesQueue([]));
  }, [work?.id, work?.series?.slug]);
  if (error) return <ErrorState title="Work not found" message={error} />;
  if (!work) return <LoadingState label="Opening the work…" />;
  const playable = player.playableAsset(work);
  const video = work.assets.find((asset) => asset.kind === "video");
  const readable = work.assets.find((asset) =>
    ["pdf", "ebook", "text"].includes(asset.kind),
  );
  const companionWorks = [
    ...(work.relations || []).map((item) => item.work),
    ...(work.related || []),
  ];
  const companionArtwork = assignArtwork(companionWorks, `work:${work.id}`);
  const relationArtwork = companionArtwork.slice(
    0,
    work.relations?.length || 0,
  );
  const relatedArtwork = companionArtwork.slice(work.relations?.length || 0);
  return (
    <article className="work-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>›</span>
        {work.series ? (
          <>
            <Link to={`/series/${work.series.slug}`}>{work.series.title}</Link>
            <span>›</span>
          </>
        ) : null}
        <span>{work.title}</span>
      </div>
      <header className="work-hero">
        <Cover work={work} size="hero" />
        <div className="work-hero__copy">
          <div className="section-kicker">
            {work.editorialStatus === "contemporary-adaptation"
              ? "CONTEMPORARY ADAPTATION"
              : work.editorialStatus === "contemporary-original"
                ? "NEW FROM ETERNALLY BLESSED"
                : "ARCHIVE ORIGINAL"}{" "}
            · {work.kind.replaceAll("-", " ")}
            {work.year ? ` · ${work.year}` : ""}
          </div>
          <h1>{work.title}</h1>
          <p className="work-hero__byline">
            {work.contributors.length
              ? work.contributors.map((person, index) => (
                  <span key={person.id}>
                    {index ? ", " : ""}
                    <Link to={`/people/${person.slug}`}>{person.name}</Link>
                  </span>
                ))
              : "Eternally Blessed archive"}
          </p>
          {(work.description || work.bodyExcerpt || "").length > 200 ? (
            <details className="work-summary">
              <summary>About this work</summary>
              <p className="work-hero__description">
                {work.description || work.bodyExcerpt}
              </p>
            </details>
          ) : (
            <p className="work-hero__description">
              {work.description ||
                work.bodyExcerpt ||
                "Preserved from the Eternally Blessed archive."}
            </p>
          )}
          <div className="work-hero__actions">
            {playable ? (
              <button
                className="primary-action"
                onClick={() =>
                  player.load(
                    work,
                    seriesQueue.length ? seriesQueue : [work],
                    new URLSearchParams(location.search).has("t")
                      ? Math.max(
                          0,
                          Number(
                            new URLSearchParams(location.search).get("t"),
                          ) || 0,
                        )
                      : undefined,
                  )
                }
              >
                ▶ {library.progress[work.id] ? "Resume" : "Play"}
              </button>
            ) : null}
            {readable ? (
              <a
                className="primary-action primary-action--paper"
                href={
                  readable.kind === "pdf" ? "#document-reader" : readable.url
                }
                target={readable.kind === "pdf" ? undefined : "_blank"}
                rel="noreferrer"
              >
                {readable.kind === "pdf"
                  ? "Read document"
                  : `Read ${formatLabel(readable.kind, readable.label)}`}
              </a>
            ) : null}
            <button
              className="secondary-action"
              onClick={() => library.toggleFavorite(work)}
            >
              {library.isFavorite(work.id) ? "◆ Saved" : "◇ Save"}
            </button>
          </div>
          {work.series ? (
            <Link
              className="series-position"
              to={`/series/${work.series.slug}`}
            >
              Part {work.series.position || "—"} of {work.series.title} →
            </Link>
          ) : null}
        </div>
      </header>
      {video ? (
        <section className="work-reader">
          <h2>Watch</h2>
          <video controls preload="metadata" src={video.url} />
        </section>
      ) : null}
      <Transcript work={work} />
      <Reader
        slug={slug}
        pdf={work.assets.find((a) => a.kind === "pdf")?.url}
      />
      <div className="work-details">
        <section>
          <h2>About this work</h2>
          {work.scriptureRefs.length ? (
            <div className="detail-group">
              <h3>Scripture</h3>
              <div className="tag-list">
                {work.scriptureRefs.map((ref) => (
                  <Link
                    key={ref.label || `${ref.book}-${ref.chapterStart}`}
                    to={`/search?book=${encodeURIComponent(ref.book)}&chapter=${ref.chapterStart}${ref.verseStart ? `&verse=${ref.verseStart}` : ""}`}
                  >
                    {ref.label || `${ref.book} ${ref.chapterStart}`}
                  </Link>
                ))}
              </div>
            </div>
          ) : null}
          {work.topics.length ? (
            <div className="detail-group">
              <h3>Topics</h3>
              <div className="tag-list">
                {work.topics.map((topic) => (
                  <Link
                    key={topic}
                    to={`/search?q=${encodeURIComponent(topic)}`}
                  >
                    {topic}
                  </Link>
                ))}
              </div>
            </div>
          ) : null}
        </section>
        <section>
          <h2>Sources and editions</h2>
          <div className="manifestations">
            {work.manifestations?.length
              ? work.manifestations.map((source) => (
                  <div key={source.id}>
                    <div>
                      <strong>{source.source.replaceAll("-", " ")}</strong>
                      <span>{source.title}</span>
                    </div>
                    <div>
                      {source.assets.map((asset) => (
                        <a
                          key={asset.id}
                          href={asset.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {formatLabel(asset.kind, asset.label)}
                        </a>
                      ))}
                    </div>
                  </div>
                ))
              : work.assets.map((asset) => (
                  <div key={asset.id}>
                    <div>
                      <strong>Archive source</strong>
                      <span>{asset.contentType || asset.kind}</span>
                    </div>
                    <div>
                      <a href={asset.url} target="_blank" rel="noreferrer">
                        {formatLabel(asset.kind, asset.label)}
                      </a>
                    </div>
                  </div>
                ))}
          </div>
          <p className="provenance-note">
            Explore the available editions and original source files.
          </p>
        </section>
      </div>
      {work.relations?.length ? (
        <section className="related">
          <div className="section-kicker">ONE BODY OF TEACHING</div>
          <h2>Original archive sources</h2>
          <div>
            {work.relations.map((item, index) => (
              <WorkCard
                key={`${item.relation}-${item.work.id}`}
                work={item.work}
                artwork={relationArtwork[index]}
                reason={item.relation.replaceAll("-", " ")}
              />
            ))}
          </div>
        </section>
      ) : null}
      {work.related?.length ? (
        <section className="related">
          <div className="section-kicker">KEEP EXPLORING</div>
          <h2>Related works</h2>
          <div>
            {work.related.map((item, index) => (
              <WorkCard
                key={item.id}
                work={item}
                artwork={relatedArtwork[index]}
              />
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
